import { useRef, useState } from 'react'
import { Hand, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MeetingParticipant } from '@hub/contracts'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { participantInitials } from '@/lib/meetings/rtk'
import { cn } from '@/lib/utils'

export type ParticipantPresence = 'active' | 'connected' | 'left'

/**
 * Who is in the meeting, from the room's own list, with what "in" means
 * (board m5gc #16): `active` = in the media session (camera/mic tracks
 * flowing), `connected` = on the room socket only (a dead tab, a page that
 * has not finished joining media), `left` = was here, gone now.
 */
export function presenceOf(
  participant: MeetingParticipant,
  mediaUserIds: ReadonlySet<string>
): ParticipantPresence {
  if (!participant.connected) return 'left'
  return mediaUserIds.has(participant.userId) ? 'active' : 'connected'
}

export function ParticipantsPopover({
  count,
  participants,
  mediaUserIds,
  selfUserId,
}: {
  count: number
  participants: MeetingParticipant[]
  mediaUserIds: ReadonlySet<string>
  selfUserId: string | undefined
}) {
  const { t } = useTranslation('meet')
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mouse: hover opens, leaving closes after a beat (so the pointer can
  // travel into the list). Touch: tap toggles, Radix handles outside taps.
  const hoverOpen = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpen(true)
  }
  const hoverClose = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    closeTimer.current = setTimeout(() => setOpen(false), 200)
  }

  const order: Record<ParticipantPresence, number> = { active: 0, connected: 1, left: 2 }
  const rows = participants
    .map((p) => ({ p, presence: presenceOf(p, mediaUserIds) }))
    .sort((a, b) => order[a.presence] - order[b.presence] || a.p.name.localeCompare(b.p.name))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('controls.participants', { count })}
          data-testid="participants-trigger"
          onPointerEnter={hoverOpen}
          onPointerLeave={hoverClose}
          className="flex h-9 items-center gap-1 rounded-full px-1.5 text-xs text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-white data-[state=open]:bg-zinc-700 data-[state=open]:text-white"
        >
          <Users className="h-3.5 w-3.5" />
          {count}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={10}
        className="w-64 p-2"
        data-testid="participants-list"
        onPointerEnter={hoverOpen}
        onPointerLeave={hoverClose}
      >
        <p className="text-muted-foreground mb-1 px-1 text-xs font-semibold uppercase">
          {t('participants.title', { count })}
        </p>
        <ul className="max-h-72 space-y-0.5 overflow-y-auto">
          {rows.map(({ p, presence }) => (
            <li key={p.userId} className="flex items-center gap-2 rounded-md px-1 py-1 text-sm">
              {p.avatarUrl ? (
                <img
                  src={p.avatarUrl}
                  alt=""
                  className="h-6 w-6 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-[10px] font-semibold text-white">
                  {participantInitials(p)}
                </span>
              )}
              <span
                className={cn(
                  'min-w-0 flex-1 truncate',
                  presence === 'left' && 'text-muted-foreground'
                )}
              >
                {p.userId === selfUserId ? t('dock.you') : p.name}
              </span>
              {p.handRaisedAt && (
                <Hand
                  className="h-3.5 w-3.5 shrink-0 text-amber-500"
                  aria-label={t('dock.handRaised')}
                />
              )}
              <span
                className={cn(
                  'flex shrink-0 items-center gap-1 text-[11px]',
                  presence === 'active' && 'text-emerald-600 dark:text-emerald-400',
                  presence === 'connected' && 'text-amber-600 dark:text-amber-400',
                  presence === 'left' && 'text-muted-foreground'
                )}
              >
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    presence === 'active' && 'bg-emerald-500',
                    presence === 'connected' && 'bg-amber-500',
                    presence === 'left' && 'bg-zinc-400'
                  )}
                />
                {t(`participants.${presence}`)}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
